<script>
import { formatMoney } from 'helpers/utils';

// Dealer Sales & Service Program Status modal.
// Opened from the Briggs & Stratton contract card on the My Account page.
//
// Markup mirrors dealer-agreement.vue (.modal-mask / .modal-wrapper / .modal-container)
// on purpose: the shared `.modal` class collides with Bootstrap's `.modal { display:none }`
// (Bootstrap CDN is loaded on the My Account page), which hid this modal.
//
// The report STRUCTURE — section titles, row labels, the Dealer Benefits matrix, and the
// tier minimums — is hard-coded in the template (program constants, same for every dealer).
// Only DEALER-SPECIFIC values come from the `payload` prop, shaped like the API response;
// the parent passes real data via :payload (getBriggsProgramStatus). `payload` defaults to
// an EMPTY structure of the same shape — a safety fallback, since the modal only renders
// once real data has loaded. Dollar figures arrive as raw numbers and render through money()
// so they always display as currency; non-numeric cells ("Qualified", "Not Required") pass
// through unchanged. A payload cell is { text, status? } (status: 'good' | 'bad').
export default {
    props: {
        show: { type: Boolean, default: false },
        contract: { type: Object, default: null }, // opened contract (for the header logo)
        payload: {
            type: Object,
            default: function () {
                return {
                    dealer: {
                        name: '',
                        accountNumber: '',
                        cityState: '',
                        currentLevel: '', // Elite | Preferred | Dealer
                        contractId: '',
                        asOf: '',
                        shipByDate: '',
                    },
                    engines: {
                        qualifyingToDate: '',
                        enginesToDate: '',
                        engineDollarsToDate: '',
                        additionalRequired: {
                            elite: { text: '' },
                            preferred: { text: '' },
                            dealer: { text: '' },
                        },
                    },
                    oilKits: {
                        vanguardOilToDate: '',
                        maintenanceKitsToDate: '',
                        additionalRequired: {
                            elite: { text: '' },
                            preferred: { text: '' },
                            dealer: { text: '' },
                        },
                    },
                    other: {
                        mstOnStaff: {
                            elite: { text: '' },
                            preferred: { text: '' },
                            dealer: { text: '' },
                        },
                        stockUpOrder: {
                            elite: { text: '' },
                            preferred: { text: '' },
                            dealer: { text: '' },
                        },
                    },
                };
            },
        },
    },

    data: function () {
        return {
            tiers: ['Elite', 'Preferred', 'Dealer'], // column order (structural constant)
        };
    },

    methods: {
        badgeSrc: function (tier) {
            return '/assets/program-status/' + (tier || '').toLowerCase() + '.svg';
        },
        statusClass: function (cell) {
            return cell && cell.status ? 'program-status__cell--' + cell.status : '';
        },
        // Format dollar figures as currency. The API sends amounts as numeric STRINGS
        // ("28426"), so coerce those to a number before formatting -> "$28,426.00".
        // Non-numeric cells ("Qualified", "Not Required", null, "") pass through unchanged.
        money: function (value) {
            if (value === null || value === undefined || String(value).trim() === '') {
                return value;
            }
            var num = typeof value === 'number' ? value : Number(String(value).replace(/[$,]/g, ''));
            return Number.isFinite(num) ? formatMoney(num) : value;
        },
        close: function () {
            this.$emit('close');
        },
    },
};
</script>

<template>
    <transition name="modal" v-if="show">
        <div class="program-status-modal">
            <div class="modal-mask" @click.self="close">
                <div class="modal-wrapper" @click.self="close">
                    <div class="modal-container">
                        <div class="modal-header">
                            <img v-if="contract" :src="'/assets/Brands/HiResLogos/' + contract.Code + '.png'" alt="Briggs &amp; Stratton" />
                            <div class="modal-header__titles">
                                <h3>Dealer Sales &amp; Service Program Status</h3>
                                <p class="program-status__asof">
                                    Information current as of <strong>{{ payload.dealer.asOf }}</strong>
                                </p>
                            </div>
                            <button class="program-status-modal__close" @click="close" aria-label="Close">
                                <i class="fas fa-times" aria-hidden="true"></i>
                            </button>
                        </div>

                        <div class="modal-body">
                            <div class="program-status">
                                <div class="program-status__dealer-name">{{ payload.dealer.name }}</div>
                                <div class="program-status__identity">
                                    <div class="program-status__id-row">
                                        <span class="program-status__id-label">Account Number</span>
                                        <span class="program-status__id-value">{{ payload.dealer.accountNumber }}</span>
                                    </div>
                                    <div class="program-status__id-row">
                                        <span class="program-status__id-label">City / State</span>
                                        <span class="program-status__id-value">{{ payload.dealer.cityState }}</span>
                                    </div>
                                    <div class="program-status__id-row program-status__id-row--level">
                                        <span class="program-status__id-label">Current Dealer Contract Level</span>
                                        <span class="program-status__id-value">
                                            <img
                                                class="program-status__badge-img program-status__badge-img--lg"
                                                :src="badgeSrc(payload.dealer.currentLevel)"
                                                :alt="payload.dealer.currentLevel"
                                            />
                                        </span>
                                    </div>
                                    <div class="program-status__id-row">
                                        <span class="program-status__id-label">Briggs &amp; Stratton Contract ID</span>
                                        <span class="program-status__id-value">{{ payload.dealer.contractId }}</span>
                                    </div>
                                </div>

                                <div class="program-status__scroll">
                                    <table class="program-status__table" :class="'program-status__table--' + payload.dealer.currentLevel.toLowerCase()">
                                        <tbody>
                                            <!-- ===== Dealer Benefits (hard-coded program matrix) ===== -->
                                            <tr class="program-status__band">
                                                <td colspan="4">Dealer Benefits</td>
                                            </tr>
                                            <tr class="program-status__tiers">
                                                <td></td>
                                                <td v-for="tier in tiers" :key="tier">
                                                    <img class="program-status__badge-img" :src="badgeSrc(tier)" :alt="tier" />
                                                </td>
                                            </tr>
                                            <tr class="program-status__brow">
                                                <td class="program-status__rowlabel">Vanguard 24-Hour Parts Guarantee</td>
                                                <td class="program-status__cell">Yes</td>
                                                <td class="program-status__cell">No</td>
                                                <td class="program-status__cell">No</td>
                                            </tr>
                                            <tr class="program-status__brow">
                                                <td class="program-status__rowlabel">Annual Stock Adjustment</td>
                                                <td class="program-status__cell">
                                                    With 2-for-1<br />
                                                    Offsetting Order
                                                </td>
                                                <td class="program-status__cell">
                                                    With 3-for-1<br />
                                                    Offsetting Order
                                                </td>
                                                <td class="program-status__cell">No</td>
                                            </tr>
                                            <tr class="program-status__brow">
                                                <td class="program-status__rowlabel">
                                                    Warranty Parts Reimbursement*
                                                    <span class="program-status__note">*Reimbursement amount subject to individual state law</span>
                                                </td>
                                                <td class="program-status__cell">List</td>
                                                <td class="program-status__cell">Cost + 20%</td>
                                                <td class="program-status__cell">Cost + 10%</td>
                                            </tr>
                                            <tr class="program-status__brow">
                                                <td class="program-status__rowlabel">Diagnostic Time Reimbursement</td>
                                                <td class="program-status__cell">30 Minutes</td>
                                                <td class="program-status__cell">No</td>
                                                <td class="program-status__cell">No</td>
                                            </tr>
                                            <tr class="program-status__brow">
                                                <td class="program-status__rowlabel">Warranty Determination Time</td>
                                                <td class="program-status__cell">Priority</td>
                                                <td class="program-status__cell">Standard</td>
                                                <td class="program-status__cell">Standard</td>
                                            </tr>
                                            <tr class="program-status__brow">
                                                <td class="program-status__rowlabel">Call Center Access</td>
                                                <td class="program-status__cell">Priority</td>
                                                <td class="program-status__cell">Yes</td>
                                                <td class="program-status__cell">Yes</td>
                                            </tr>
                                            <tr class="program-status__brow">
                                                <td class="program-status__rowlabel">Top Dealer Locator Listing</td>
                                                <td class="program-status__cell">Priority</td>
                                                <td class="program-status__cell">Recommended</td>
                                                <td class="program-status__cell">Standard</td>
                                            </tr>
                                            <tr class="program-status__brow">
                                                <td class="program-status__rowlabel">Locally Program Inclusion</td>
                                                <td class="program-status__cell">Priority</td>
                                                <td class="program-status__cell">Offered</td>
                                                <td class="program-status__cell">Offered</td>
                                            </tr>

                                            <!-- ===== Engines (minimums hard-coded, figures from payload) ===== -->
                                            <tr class="program-status__band">
                                                <td colspan="4">Engines, Engine Parts and Accessories</td>
                                            </tr>
                                            <tr>
                                                <td class="program-status__rowlabel">Minimum Annual Purchases</td>
                                                <td class="program-status__cell">$40,000</td>
                                                <td class="program-status__cell">$7,000</td>
                                                <td class="program-status__cell">$200</td>
                                            </tr>
                                            <tr>
                                                <td class="program-status__rowlabel">April 1 to Date Total Qualifying Purchases</td>
                                                <td colspan="3" class="program-status__cell program-status__cell--span">
                                                    {{ money(payload.engines.qualifyingToDate) }}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td class="program-status__rowlabel">
                                                    Additional Purchases Required to Ship by {{ payload.dealer.shipByDate }}
                                                </td>
                                                <td class="program-status__cell" :class="statusClass(payload.engines.additionalRequired.elite)">
                                                    {{ money(payload.engines.additionalRequired.elite.text) }}
                                                </td>
                                                <td class="program-status__cell" :class="statusClass(payload.engines.additionalRequired.preferred)">
                                                    {{ money(payload.engines.additionalRequired.preferred.text) }}
                                                </td>
                                                <td class="program-status__cell" :class="statusClass(payload.engines.additionalRequired.dealer)">
                                                    {{ money(payload.engines.additionalRequired.dealer.text) }}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td class="program-status__rowlabel">April 1 to Date Engines Purchased</td>
                                                <td colspan="3" class="program-status__cell program-status__cell--span">{{ payload.engines.enginesToDate }}</td>
                                            </tr>
                                            <tr>
                                                <td class="program-status__rowlabel">April 1 to Date Engine $$ Purchased</td>
                                                <td colspan="3" class="program-status__cell program-status__cell--span">
                                                    {{ money(payload.engines.engineDollarsToDate) }}
                                                </td>
                                            </tr>

                                            <!-- ===== Vanguard / Oil (minimums hard-coded, figures from payload) ===== -->
                                            <tr class="program-status__band">
                                                <td colspan="4">Vanguard Oil and Maintenance Kits</td>
                                            </tr>
                                            <tr>
                                                <td class="program-status__rowlabel">Minimum Annual Purchases</td>
                                                <td class="program-status__cell">$4,000</td>
                                                <td class="program-status__cell">$0</td>
                                                <td class="program-status__cell">$0</td>
                                            </tr>
                                            <tr>
                                                <td class="program-status__rowlabel">April 1 to Date Total Vanguard Oil Purchased</td>
                                                <td colspan="3" class="program-status__cell program-status__cell--span">
                                                    {{ money(payload.oilKits.vanguardOilToDate) }}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td class="program-status__rowlabel">April 1 to Date Total Maintenance Kits Purchased</td>
                                                <td colspan="3" class="program-status__cell program-status__cell--span">
                                                    {{ money(payload.oilKits.maintenanceKitsToDate) }}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td class="program-status__rowlabel">
                                                    Additional Purchases Required to Ship by {{ payload.dealer.shipByDate }}
                                                </td>
                                                <td class="program-status__cell" :class="statusClass(payload.oilKits.additionalRequired.elite)">
                                                    {{ money(payload.oilKits.additionalRequired.elite.text) }}
                                                </td>
                                                <td class="program-status__cell" :class="statusClass(payload.oilKits.additionalRequired.preferred)">
                                                    {{ money(payload.oilKits.additionalRequired.preferred.text) }}
                                                </td>
                                                <td class="program-status__cell" :class="statusClass(payload.oilKits.additionalRequired.dealer)">
                                                    {{ money(payload.oilKits.additionalRequired.dealer.text) }}
                                                </td>
                                            </tr>

                                            <!-- ===== Other (statuses from payload) ===== -->
                                            <tr class="program-status__band">
                                                <td colspan="4">Other</td>
                                            </tr>
                                            <tr>
                                                <td class="program-status__rowlabel">MST On Staff (required for Elite status)</td>
                                                <td class="program-status__cell" :class="statusClass(payload.other.mstOnStaff.elite)">
                                                    {{ payload.other.mstOnStaff.elite.text }}
                                                </td>
                                                <td class="program-status__cell" :class="statusClass(payload.other.mstOnStaff.preferred)">
                                                    {{ payload.other.mstOnStaff.preferred.text }}
                                                </td>
                                                <td class="program-status__cell" :class="statusClass(payload.other.mstOnStaff.dealer)">
                                                    {{ payload.other.mstOnStaff.dealer.text }}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td class="program-status__rowlabel">PD Stock-Up Order Placed</td>
                                                <td class="program-status__cell" :class="statusClass(payload.other.stockUpOrder.elite)">
                                                    {{ payload.other.stockUpOrder.elite.text }}
                                                </td>
                                                <td class="program-status__cell" :class="statusClass(payload.other.stockUpOrder.preferred)">
                                                    {{ payload.other.stockUpOrder.preferred.text }}
                                                </td>
                                                <td class="program-status__cell" :class="statusClass(payload.other.stockUpOrder.dealer)">
                                                    {{ payload.other.stockUpOrder.dealer.text }}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>
